import { FC, useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { featureFlags } from 'src/config/featureFlags';
import { cn } from 'src/utils/utility';
import { useSidebar } from './useSidebar';
import { LayoutSidebarProps } from './types';
// Fix import to use named export instead of default export
import { SidebarNav } from './SidebarNav';

export const LayoutSidebar: FC<LayoutSidebarProps> = ({ children }) => {
  const location = useLocation();
  const isLandingPage = location.pathname === '/' && featureFlags.SHOW_SIDEBAR_NAV;

  // Helper function to check if we're on a detail page that should have collapsed sidebar
  const isDetailPage = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);

    // Check for analytics detail page - using query parameter 'report'
    if (location.pathname.includes('/analytics') && searchParams.has('report')) {
      return true;
    }

    // Check for performance assessment detail page - using either path or query param
    if (location.pathname.includes('/performance') && (searchParams.has('id') || searchParams.has('report'))) {
      return true;
    }
    if (location.pathname.includes('/performance/') && location.pathname.split('/').length > 3) {
      return true;
    }

    // Check for rate card manager detail pages - multiple possible URL patterns
    if (location.pathname.includes('/rate-card') && searchParams.has('id')) {
      return true;
    }
    if (location.pathname.includes('/rate-card/') && location.pathname.split('/').length > 3) {
      return true;
    }
    if (location.pathname.includes('/ratecard-manager') && (searchParams.has('id') || searchParams.has('report'))) {
      return true;
    }

    return false;
  }, [location.pathname, location.search]);
  // Helper function to determine padding class based on sidebar visibility and collapsed state
  const getPaddingClass = (showSidebar: boolean, isSidebarCollapsed: boolean): string => {
    if (!showSidebar) return '';
    // Match the 300ms duration of SidebarNav component for synchronized transitions
    let styles = 'py-10 pr-10 xl:pr-10 h-[calc(100vh_-_64px)] xl:h-[calc(100vh_-_84px)] transition-all duration-300 ease-in-out ';
    styles += isSidebarCollapsed ? 'pl-[92px]' : 'pl-[260px] xl:pl-[280px]';
    return styles;
  };

  const getContentClass = (showSidebar: boolean): string => {
    if (!showSidebar) {
      return '';
    }
    // Base styles for all pages
    let styles = 'border border-neutral-20 rounded-12 h-full overflow-y-auto flex flex-col';

    // Only add bg-white class when on landing page
    if (isLandingPage) {
      styles += ' bg-white';
    }

    return styles;
  };

  // Use the sidebar context instead of local state
  const { isCollapsed, setSidebarCollapsed } = useSidebar();

  // Store previous path to detect navigation changes
  const [previousPath, setPreviousPath] = useState(location.pathname + location.search);

  // Only auto-collapse when first navigating to a detail page
  useEffect(() => {
    const currentPath = location.pathname + location.search;

    // Only trigger when the path actually changes to avoid constant checks
    if (currentPath !== previousPath) {
      // Update the previous path first
      setPreviousPath(currentPath);

      // Only auto-collapse when NEWLY navigating to a detail page
      if (isDetailPage() && !isCollapsed) {
        // Add a small delay for a smoother transition
        setTimeout(() => {
          setSidebarCollapsed(true);
        }, 300);
      }
    }
  }, [location.pathname, location.search, previousPath, isCollapsed, isDetailPage, setSidebarCollapsed]);
  // Added missing dependencies to fix ESLint warning

  // Toggle sidebar state using context
  const handleToggleSidebar = () => {
    setSidebarCollapsed(!isCollapsed);
  };

  return (
    <div className='flex-1 flex min-h-[calc(100vh_-_82px)] pt-[62px] xl:pt-[82px]'>
      {featureFlags.SHOW_SIDEBAR_NAV && (
        <div className='pb-12 p-10 fixed top-[62px] xl:top-[82px] bottom-0 left-0 z-20'>
          <SidebarNav collapsed={isCollapsed} setCollapsed={handleToggleSidebar} isLandingPage={isLandingPage} />
        </div>
      )}
      <div className={cn('flex flex-col w-full transition-all duration-300 ease-in-out', getPaddingClass(featureFlags.SHOW_SIDEBAR_NAV, isCollapsed))}>
        <div className={cn(getContentClass(featureFlags.SHOW_SIDEBAR_NAV), 'transition-all duration-300')}>{children}</div>
      </div>
    </div>
  );
};
